<template>
<Tile class="opc-zeduschp">
  <edu-switch-tip v-if="$context.is.edu2esc" :plan="$t('billing.zone_edu_plus.title')"/>
  <Foldable v-model="expanded" :title="title">
    <Header :class="['opc-zeduschp__header', !expanded ? 'opc-zeduschp__header__expand' : '']" slot="head" :logo="logo" :title="title" :ready="!licenseWarning">
      <div>
        <zm-button data-testid="checkout-button-97" type="link" @click="openDetails" v-if="expanded && planDetailLabel">{{ planDetailLabel }}</zm-button>
      </div>
      <div class="opc-zm-base-card__collapse" v-if="!expanded" v-html="planSummary"></div>
    </Header>

    <div class="opc-zeduschp-main">
      <h2 class="opc-zeduschp-select-license">{{ $t('billing.zone.select_license') }}</h2>
      <p class="opc-zeduschp-select-license-desc">
        {{ $t('billing.zobizplus.unlimited_desc') }}
        <span v-if='isShowLeastDesc'>
          {{ $t( 'billing.zone_edu_plus.at_least_desc', [20] )}}
        </span>
        <zm-button data-testid="checkout-button-98" type="link" @click="onExploreOtherPlan" v-if="planDetailLabel">
          {{ $t('billing.zone.explore_other_plans') }}
        </zm-button>
      </p>

      <Alert v-if="licenseWarning" class="opc-zeduschp-warn" type="warning" :closable="false">
        <p v-html="licenseWarning" @click="onBannerLinkClick($event)"></p>
      </Alert>

      <NumberSelect
        ref="regionsSelect"
        :label="$t('billing.zobizplus.UC')"
        unit=""
        v-model="callingSelected"
        :optionList="callingOptions"
        :deletable="callingSelected.length > 1"
        :title="$t('billing.pbx.calling_plans')"
        :selectTitle="$t('billing.pbx.calling_regions')"
        :inputTitle="$t('billing.pbx.user_number')"
        @optionSelected="onRegionSelected"
      />
      <zm-button data-testid="checkout-button-99" type="link" v-if="addRegionVisible" @click="onAddRegion">
        {{ $t('billing.zobizplus.add_another') }}
      </zm-button>

      <zm-alert class="opc-zeduschp-license-tip" v-if="licenseTip" type="info" :closable="false">
        <i class="zm-icon-info" style="color: #9191A4;"></i>
        <p v-html="licenseTip" @click="onBannerLinkClick($event)"></p>
      </zm-alert>

      <CascadeCycle v-if="cycleVisible" class="opc-zeduschp-cycle" :config="cascadeCycleConfig" v-model="cycleAndUsageFee" :trackingData="cycleTrackingData">
        <div slot="group1-title">
          <span id="group1-title">{{ $t('billing.pbx.calling_fee_label.metered') }}</span>
          <zm-tooltip placement="top" tab-to-leave>
            <p v-html="$t('billing.pbx.calling_fee_tips', [$t('billing.pbx.regions.US')])" slot="content"></p>
            <zm-button data-testid="checkout-button-100" :aria-label="`Learn More about ${$t('billing.pbx.calling_fee_label.metered')} `" type="text" ghost size="mini" style="top: -2px">
              <i class="zm-icon-info-outline" />
            </zm-button>
          </zm-tooltip>
        </div>
      </CascadeCycle>

      <AppealCycle v-if="planBillInfo" :cycle="i18n_cycle(planBillInfo.billing_cycle)" :payment="planBillInfo.payment" @click="onAppealClick" />
    </div>

    <div class="opc-zm-base-card__main__footer" v-if="isShowRemoveBtn">
      <zm-button data-testid="checkout-button-101" type="link" ghost @click="removeAllPlans">{{ $t('billing.common.remove') }}</zm-button>
    </div>
  </Foldable>
</Tile>
</template>

<script>
import { mapMutations, mapGetters } from 'vuex';
import { debounce, groupBy, get } from 'lodash';

import plansMixin from '@/plans/mx/plan';
import AddonMixin from '@/plans/mx/addon';
import zEduMixin from '@/plans/ZONE_EDU_PLUS/mixin';

import { PBX_REGIONS, regions } from '@/plans/ZOOM_PHONE/helper/constants';
import { getZPCallingFeesText } from '@/plans/ZOOM_PHONE/helper/utils';
import { plansof } from '@/utils/chain';
import { BillingCycleEnum, limitBillingCycleWithBaseZoneTypes, BILLING_CYCLE, planTypes, ZOOM_MEETING_EDU_PLUS_MIN_LICENSE, ZOOM_MEETING_EDU_PLUS_MAX_LICENSE } from "@/common/constants";
import { computedCallingPlans, computedPbxPlans, computedPbxBasePlans } from '@/common/service/computers/plans/pbx';
import { i18n_cycle } from '@/utils/i18n-shortcuts';
import { checkSelfLicenseNum, getSelfTotalLicenseAmount } from '../utils';
import { $context } from '@/global-install';
import { ZOOM_MEETING_SUB_TYPES } from '@/plans/ZOOM_MEETING/common/utils';

import Tile from '@/components/tile';
import Foldable from '@/components/foldable'
import Alert from '@/components/zoom-ui-plus/alert/alert.vue';
import { ServiceConfirm } from '@/components/common/service-dialog/confirm';
import NumberSelect from '@/plans/ZOOM_ONE_BUNDLE/component/select-number-list.vue';
import Header from '@/plans/ZOOM_ONE_BUNDLE/header.vue';
import CascadeCycle from '@/plans/ZOOM_PHONE/component/opc-card-cascade.vue';
import AppealCycle from '@/plans/ZOOM_PHONE/component/appeal-cycle.vue';

import PlanDetail from '../dialog/plan-detail.vue'
import planComparison from '../dialog/full-comparison.vue'
import { isFromShoppingCart, isProductCanRemove } from '@/views/config/shopping-cart/persist/plan-remove';
import eduSwitchTip from '@/components/edu-switch-tip.vue';

export default {
  mixins: [plansMixin, AddonMixin, zEduMixin],
  components: {
    Tile,
    Foldable,
    Header,
    Alert,
    NumberSelect,
    CascadeCycle,
    AppealCycle,
    eduSwitchTip
  },
  data() {
    return {
      expanded: true,
      licenseWarning: '',
      planDetailDialog: new ServiceConfirm(PlanDetail),
      comparisonDialog: new ServiceConfirm(planComparison),
      callingSelected: [],

    }
  },
  inject: ['$core'],
  computed: {
    ...mapGetters([
      'getSelectedPlans',
      'plancodeMapSubscriptionDisabled',
      'fromCampaignLink',
      'getUserCurrentSubscriptions',
      'disableMonthly2AnnualTip',
      'pbxBaseInfo',
      'limitLicenseWithBaseZoneTypes'
    ]),
    isShowLeastDesc() {
      return true;
    },
    selfPlan() {
      return plansof(this.getSelectedPlans).type(planTypes.ZONE_EDU_PLUS).get();
    },
    pbxPlan() {
      return plansof(this.getSelectedPlans).type(planTypes.ZOOM_PHONE).get(0)
    },
    boughtSelfPlans() {
      return plansof(this.boughtPlans).type(planTypes.ZONE_EDU_PLUS).get()
    },
    boughtPbxPlan() {
      return get(computedPbxPlans(this.boughtPlans), '0', {})
    },
    totalLicenseAmount() {
      return getSelfTotalLicenseAmount()
    },
    disableDownSell() {
      return this?.basicConfig?.downSellData?.disableDownSell;
    },
    isBulkLicenseAccount() {
      return this.basicConfig?.isBulkLicenseAccount;
    },

    cycleVisible() {
      // if has purchased zp base plan, return false
      return !(computedPbxBasePlans(this.boughtPlans??[])?.[0] ?? false)
    },
    planBillInfo() {
      if (this.cycleVisible) return false

      // if has purchased pbx base
      const pbxBasePlan = computedPbxBasePlans(this.boughtPlans)?.[0] ?? {};
      const payment = getZPCallingFeesText(pbxBasePlan).title;

      const planWithCalling = computedCallingPlans(this.boughtPlans)?.[0] ?? {};
      const billing_cycle = pbxBasePlan.billing_cycle === BILLING_CYCLE.PAY_AS_YOU_GO ? BILLING_CYCLE.MONTHLY : planWithCalling.billing_cycle;

      return {
        billing_cycle,
        payment,
      }
    },
    cascadeCycleConfig() {
      const pbxBasePlanByCycle = groupBy(computedPbxBasePlans(this.planMapFlat), 'billing_cycle');
      const { payAsYouGo } = pbxBasePlanByCycle

      const costOf = (c) => plansof(this.planMapFlat).type(planTypes.ZONE_EDU_PLUS).cycle(c).clone().region(this.callingSelected?.[0]?.id).get(0)?.amount || 0
      const cycleList = [BILLING_CYCLE.MONTHLY, BILLING_CYCLE.ANNUAL]
      const costByCycle = cycleList.reduce((memo, c) => {
        memo[c] = costOf(c)
        return memo
      }, {})

      return {
        group0: {
          title: this.$t('billing.select_billing_cycle'),
        },
        group1: {
          slot: 'group1-title',
        },
        options: cycleList.map((c) => ({
          value: c,
          title: this.$t(`billing.billing_cycle.${c}`),
          description: this.$t(`billing.common.per_billing_cycle_user.${c}`),
          subTitle: this.$money(costByCycle[c], { forceDiff: true }),
          disabled: this.isCycleDisabled(c),
          ...costByCycle,
          options: [
            ...payAsYouGo,
            ...pbxBasePlanByCycle[c]
          ].map((p) => ({
            billing_cycle: p.billing_cycle,
            value: p.plan_code,
            subTitle: this.$money(p.amount, { forceDiff: true }),
            title: getZPCallingFeesText(p).title,
            description: getZPCallingFeesText(p).unit,
          }))
        }))
      }
    },
    cycleAndUsageFee: {
      get() {
        return [this.pbxBaseInfo.billingCycle, this.pbxBaseInfo.basePlanCode];
      },
      set(val) {
        this.setPbxBillingCycle(val[0]);
        this.setPbxBasePlanCode(val[1]);
      }
    },
    planSummary() {
      const callingPlanLabels = this.selfPlan?.map((plan) => {
        return `${this.$t(`billing.pbx.cart_name.${plan.pbxCategory}`)}  •  ${plan.cartInfo.selected_number} ${this.$t(
          'billing.planform.unit.user',
        )}  •  ${this.$money(plan.amount)}${this.$t(`billing.common.per_billing_cycle_user.${plan.billing_cycle}`)}`;
      });

      const [ billingCycle, usageFeePlanCode ] = this.cycleAndUsageFee;
      const usageFeeOption = this.cascadeCycleConfig.options.find((x) => x.value === billingCycle)?.options.find((x) => x.value === usageFeePlanCode);
      const usageFeeLabel = `${usageFeeOption?.title}  •  ${this.$t('billing.pbx.calling_fee_name.pay_you_go.MC')}  •  ${usageFeeOption?.subTitle}${usageFeeOption?.description}`;

      return [...callingPlanLabels, usageFeeLabel].join('<br/>');
    },
    selectedJSON() {
      return JSON.stringify({
        [planTypes.ZONE_EDU_PLUS]: this.callingSelected.map(x => ({
          plan_code: plansof(this.planMapFlat).type(planTypes.ZONE_EDU_PLUS).region(x.id).cycle(this.pbxBaseInfo.billingCycle).get(0)?.plan_code,
          number: x.number
        })),
        [planTypes.ZOOM_PHONE]: [
          {
            plan_code: this.pbxBaseInfo.basePlanCode,
            number: 1,
          },
        ],
      })
    },
    selectedCallingJSON() {
      return JSON.stringify(
        this.callingSelected.map(x => ({
          plan_code: plansof(this.planMapFlat).type(planTypes.ZONE_EDU_PLUS).region(x.id).cycle(this.pbxBaseInfo.billingCycle).get(0)?.plan_code,
          name: x.name,
          number: x.number
        }))
      )
    },

    planDetailLabel() {
      if (this.isBulkLicenseAccount) return ''; // cannot switch plan, so hide it
      return this.$t('billing.plan_details');
    },
    addRegionVisible() {
      return this.callingSelected?.length !== this.callingOptions?.length;
    },

    minCallingPlan(){
      return this.selfPlan?.length === 1 ? ZOOM_MEETING_EDU_PLUS_MIN_LICENSE : 1
    },
    maxCallingPlan() { // basically, 'buy now/add2cart' has an upper limit
      return ZOOM_MEETING_EDU_PLUS_MAX_LICENSE
    },
    eduPlusPlans() {
      return this.planMapFlat.filter(item => item.type === planTypes.ZONE_EDU_PLUS);
    },
    pbxRegions() {
      return regions
        .filter((x) => {
          if (x.key === PBX_REGIONS.JP) return false;
          if (this.$config('hideZoomPhoneUKIRPlans')) return x.key !== PBX_REGIONS.UK_AND_IRELAND;
          return true;
        })
        .filter(x => this.eduPlusPlans.find(item => item.region === x.key))
    },
    callingOptions() {
      return this.pbxRegions.map((x) => ({
        id: x.key,
        name: this.$t(x.label),
        desc: '',
        min: this.minCallingPlan,
        max: this.maxCallingPlan,
      }));
    },
    licenseTip() {
      return ''
    },
    cycleTrackingData() {
      return [
        {
          buyInteraction: 'subscription cycle',
          getBuySelection: (c) => {
            return `${this.trackingPlanName} - ${c}`
          },
        },
        {
          buyInteraction: `${this.trackingPlanName} calling fees`,
          getBuySelection: (c, f) => {
            const opt = this.cascadeCycleConfig?.options?.find(a => a.value === c)?.options.find(a => a.value === f)
            if (opt.billing_cycle === BILLING_CYCLE.PAY_AS_YOU_GO) {
              return `pay as you go`
            }
            return `${opt.title} ${opt.subTitle}${opt.description}`
          },
        }
      ]
    },
    isShowRemoveBtn () {
      // For zoom phone can buy independently, just think about zoom workplace pro plus dependency
      if(isFromShoppingCart()) {
        return isProductCanRemove(this.selfPlan[0])
      }
      return false;
    }
  },
  watch: {
    expanded(nv) {
      this.$tracking.buyInteraction('product details accordion', `${this.trackingPlanName} - ${nv ? 'expand' : 'collapse'}`);
    },
    getSelectedPlans: {
      immediate: true,
      handler() {
        if ($context.is.edit) return

        if ($context.is.add2cart && ((this.boughtPbxPlan && !this.boughtSelfPlans.length) || this.boughtSelfPlans.length > 0)) return;

        this.licenseWarning = checkSelfLicenseNum(ZOOM_MEETING_EDU_PLUS_MIN_LICENSE, ZOOM_MEETING_EDU_PLUS_MAX_LICENSE, true)?.message
      }
    },
    selectedJSON(nv, ov) {
      const [olds, news] = [ov, nv].map((json) => {
        return [].concat(...Object.values(JSON.parse(json)));
      });
      this.updateSelectPlans(olds.map((x) => ({ plan_code: x.plan_code })));
      this.updateSelectPlans(news.map((x) => ({ plan_code: x.plan_code, cartInfo: { selected_number: x.number } })));
    },

    selectedCallingJSON: {
      handler: debounce(function (nv, ov) {
        const [olds, news] = [ov, nv].map((json) => JSON.parse(json))
        if (olds?.length === news?.length) {
          news.forEach((c) => {
            const o = olds.find((o) => o.plan_code === c.plan_code && c.number != o.number)
            if (o) {
              const action = c.number > o.number ? 'increase' : 'decrease'
              this.$tracking.buyInteraction('number of users', `${this.trackingPlanName} - ${action} - ${c.number} - ${c.name}`)
            }
          })
        }
      }, 500)
    },
    licenseWarning(nv) {
      if (nv) {
        // remove html tags
        this.$tracking.buyInteraction(`banner - product level`, `${this.trackingPlanName} - ${nv.replace(/(<([^>]+)>)/ig, '')} - impression`)
      }
    },
    licenseTip(nv) {
      if (nv) {
        this.$tracking.buyInteraction(`banner - product level`, `${this.trackingPlanName} - ${nv.replace(/(<([^>]+)>)/ig, '')} - impression`)
      }
    }
  },
  methods: {
    i18n_cycle,

    ...mapMutations(['updateSelectPlans', 'setPbxBasePlanCode', 'setPbxBillingCycle', 'batchRemovePlan']),

    async viewDetails() {
      await this.planDetailDialog.confirm({
        bizCategoryCode: this.selfPlan?.[0]?.category,
        largeMeetingCb: this.$context.is.basic2pro || this.$context.is.buyNow ? this.largeMeetingCallback : null,
        fullListCb: () => {
          return this.openFullList().catch(() => {
            // when the customer in modal checkout, need allow customer go back to the view plan details from the full compare dialog
            this.$context.is.modal && this.viewDetails();
          })
        },
      })
    },
    onExploreOtherPlan() {
      this.$tracking.buyInteraction('explore other plans', `${this.trackingPlanName} - explore other plans`)
      this.openFullList()
    },
    openFullList() {
      return this.comparisonDialog?.confirm({
        planCategory: ZOOM_MEETING_SUB_TYPES.ZONE_EDU_PLUS,
        largeMeetingConfig: {
          school: {
            callback: () => {},
            disabled: true,
            display: this.$context.is.basic2pro || this.$context.is.buyNow
          },
          schoolPlus: {
            callback: this.largeMeetingCallback.bind(this),
            disabled: false,
            display: this.$context.is.basic2pro || this.$context.is.buyNow
          },
        },
      })
    },
    largeMeetingCallback() {
      this.$core.addon(planTypes[planTypes.LARGE_MEETING])
    },
    onAddRegion() {
      this.$tracking.buyInteraction('number of users', `${this.trackingPlanName} - add another region`)

      this.$refs.regionsSelect.newItem();
    },
    isCycleDisabled(c) {
      const activeCycle = this.cycleAndUsageFee[0]

      if (!activeCycle) return false
      if (activeCycle === c && c === BILLING_CYCLE.ANNUAL) return false

      const limitedCycleAddon = limitBillingCycleWithBaseZoneTypes.concat([])
      const exceedCycleAddon = this.getUserCurrentSubscriptions.filter(item => limitedCycleAddon.includes(item.type) && item.cycleValue > BillingCycleEnum[c])
      if (exceedCycleAddon.length) {
        if (c === BILLING_CYCLE.MONTHLY) {
          const planGroupByType = groupBy(exceedCycleAddon, 'type');
          const typeNames = Object.keys(planGroupByType).map(type =>this.$t(get(planGroupByType[type], '0.typeNameKey', ''))).filter(Boolean);

          return this.$t('billing.planform.monthly_base_disable_tooltip', [typeNames.join(','), this.$t('billing.planform.title.zoom_meeting_zoomonenew')]);
        }
        return true;
      }

      if (this.disableMonthly2AnnualTip && c === BILLING_CYCLE.ANNUAL) {
        return this.disableMonthly2AnnualTip;
      }

      return false;
    },

    getCallingPlanDisable() {
      return {
        disabled: false,
        tooltip: '',
      }
    },

    onBannerLinkClick(e) {
      if (e.target.tagName?.toUpperCase() === 'A') {
        this.$tracking.buyInteraction('banner - product level', `${this.trackingPlanName} - ${e.currentTarget.textContent} - ${e.target.textContent}`)
      }
    },
    onRegionSelected(region) {
      if (region) {
        this.$tracking.buyInteraction('number of users', `${this.trackingPlanName} - select region - ${region.name}`)
      }
    },
    onAppealClick(e) {
      if (e.target.tagName?.toUpperCase() === 'A') {
        this.$tracking.buyInteraction('contact us', `${this.trackingPlanName} - contact us`)
      }
    },
    removeAllPlans () {
      this.removePlanByKey(planTypes.zone_edu_plus)
      const zoomPhonePlans = computedPbxPlans(this.getSelectedPlans)
      this.batchRemovePlan(zoomPhonePlans)
    },
    openDetails(){
      this.$context.is.edu2esc? this.onExploreOtherPlan() :this.viewDetails()
    }
  },
  created() {
    this.cycleAndUsageFee = [this.selfPlan?.[0]?.billing_cycle || BILLING_CYCLE.MONTHLY, this.pbxPlan?.plan_code]
    this.callingSelected = this.selfPlan?.map((x) => {
      return {
        id: x.region,
        number: x.cartInfo?.selected_number,
        plan_code: x.plan_code,
        inputTooltip: '',
        shouldDisabled: false,
        disableSwitch: false,
        disableDelete: false,
        deleteTips: '',
        ...this.getCallingPlanDisable(x)
      }
    });
  },
}
</script>

<style lang="scss">
@import '@/styles/_variables.scss';

.opc-zeduschp {
  .opc-zeduschp__header {
    &.opc-zm-base-card__head {
      padding-right: 32px;
      display: flex;

      .opc-zm-base-card__name {
        align-items: baseline;
        .zm-icon-success {
          align-self: initial; // force override global styles(center)
        }
      }
    }

    &.opc-zeduschp__header__expand {
      padding-right: 80px;
    }
  }
  .opc-zm-base-card__name.text-capitalize {
    text-transform: none;
  }
  &-main {
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid #dfdfe4;
    .opc-zeduschp-warn {
      margin-bottom: 12px;
    }
  }
  &-select-license {
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: 35px;
    color: #00031F;
  }
  &-select-license-desc {
    margin-bottom: 12px;
    font-size: 14px;
    font-weight: 500;
    line-height: 21px;
    color: #666487;
    .zm-button--link {
      padding: 0;
    }
  }
  &-cycle {
    margin-top: 24px;
  }
  &-license-tip {
    margin-top: 12px;
    .zm-alert__description {
      display: flex;
      align-items: center;
      column-gap: 12px;
    }
  }
  &-detail-tip {
    margin-top: 10px;
    padding: 8px 12px;
    background: #F2F2F4;
    font-family: Almaden Sans;
    font-size: 14px;
    font-weight: 500;
    line-height: 21px;
    border-radius: 8px;
    color: #515079;
  }
}
</style>
